import * as XLSX from 'xlsx'

export const FILTERS_STORAGE_KEY = 'managementMisFilters'
export const UNAVAILABLE_TEXT = 'Data unavailable'
export const EMAIL_DISABLED_NOTE = 'Automated management email scheduling is pending — use export for now.'

const METRIC_STATUSES = ['actual', 'zero', 'missing', 'unavailable', 'masked']
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i

// ---------- metrics ----------

export const isMetric = (m) =>
  Boolean(m) && typeof m === 'object' && !Array.isArray(m) && METRIC_STATUSES.includes(m.status)

export const isAvailable = (m) => {
  if (typeof m === 'number') return Number.isFinite(m)
  return isMetric(m) && (m.status === 'actual' || m.status === 'zero')
}

/** Numeric value of a metric, or null when it is unavailable / missing / masked. */
export const metricNumber = (m) => {
  if (typeof m === 'number') return Number.isFinite(m) ? m : null
  if (!isAvailable(m)) return null
  return typeof m.value === 'number' ? m.value : null
}

const MASKED = Object.freeze({ status: 'masked' })

export const isMasked = (v) => Boolean(v) && typeof v === 'object' && v.status === 'masked'

/**
 * Safe path lookup ('cash.netCashFlow.net'). The backend replaces a whole
 * sensitive branch with { status: 'masked' }, so a masked ancestor masks the leaf.
 */
export const at = (obj, path) => {
  let cur = obj
  for (const key of String(path).split('.')) {
    if (isMasked(cur)) return MASKED
    if (cur === null || cur === undefined) return undefined
    cur = cur[key]
  }
  return cur
}

export const formatINR = (v, { compact = false } = {}) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return UNAVAILABLE_TEXT
  const n = Number(v)
  if (compact && Math.abs(n) >= 100000) {
    const abs = Math.abs(n)
    const sign = n < 0 ? '-' : ''
    if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`
    return `${sign}₹${(abs / 100000).toFixed(2)} L`
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(n)
}

export const formatNumber = (v, dp = 0) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return UNAVAILABLE_TEXT
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: dp, minimumFractionDigits: 0 }).format(Number(v))
}

export const formatDate = (d) => {
  if (!d) return '—'
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const formatDateTime = (d) => {
  if (!d) return '—'
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const formatRaw = (value, format) => {
  if (typeof value !== 'number') return value === null || value === undefined ? UNAVAILABLE_TEXT : String(value)
  switch (format) {
    case 'currency': return formatINR(value)
    case 'compactCurrency': return formatINR(value, { compact: true })
    case 'percent': return `${formatNumber(value, 1)}%`
    case 'signedPercent': return `${value > 0 ? '+' : ''}${formatNumber(value, 1)}%`
    case 'months': return `${formatNumber(value, 1)} mo`
    case 'days': return `${formatNumber(value, 0)} days`
    case 'hours': return `${formatNumber(value, 1)} h`
    default: return formatNumber(value, 2)
  }
}

/**
 * Render-ready view of a metric. Never invents a number: unavailable, missing and
 * masked metrics all render as "Data unavailable" with a Grey tone.
 */
export const formatMetric = (m, format = 'number') => {
  if (typeof m === 'number') return { text: formatRaw(m, format), status: 'actual', available: true, note: null }
  if (!isMetric(m)) {
    if (m && typeof m === 'object' && 'value' in m && typeof m.value === 'number') {
      return { text: formatRaw(m.value, format), status: 'actual', available: true, note: m.note || null }
    }
    return { text: UNAVAILABLE_TEXT, status: 'missing', available: false, note: null }
  }
  if (m.status === 'masked') {
    return { text: UNAVAILABLE_TEXT, status: 'masked', available: false, note: 'Restricted — requires additional Management MIS permission' }
  }
  if (m.status === 'unavailable' || m.status === 'missing') {
    return { text: UNAVAILABLE_TEXT, status: m.status, available: false, note: m.note || null }
  }
  const value = m.value
  const text = typeof value === 'number' ? formatRaw(value, format) : String(value ?? UNAVAILABLE_TEXT)
  return { text, status: m.status, available: true, note: m.note || null }
}

export const metricText = (m, format) => formatMetric(m, format).text

// ---------- KPI catalogue (keys from backend buildKpis) ----------

const R = '/management/reports'

/** direction: 1 = higher is better, -1 = lower is better, 0 = neutral. */
export const KPI_META = {
  cashBookBalance: { label: 'Cash (book balance)', format: 'currency', direction: 1, group: 'Finance', to: `${R}/financial-health` },
  verifiedCash: { label: 'Verified cash', format: 'currency', direction: 1, group: 'Finance', to: `${R}/financial-health` },
  monthlyBurn: { label: 'Monthly burn (30d)', format: 'currency', direction: -1, group: 'Finance', to: `${R}/expense-budget` },
  runwayMonths: { label: 'Runway', format: 'months', direction: 1, group: 'Finance', to: `${R}/financial-health` },
  projectedRunwayMonths: { label: 'Projected runway', format: 'months', direction: 1, group: 'Finance', to: `${R}/financial-health` },
  budgetVariancePercent: { label: 'Spend vs budget (YTD)', format: 'signedPercent', direction: -1, group: 'Finance', to: `${R}/expense-budget` },
  creditNotes: { label: 'Credit notes', format: 'currency', direction: 0, group: 'Finance' },
  revenuePeriod: { label: 'Revenue (period)', format: 'currency', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  revenueYtd: { label: 'Revenue (FY to date)', format: 'currency', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  invoicedMtd: { label: 'Invoices MTD (pre-tax)', format: 'currency', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  invoicedYtd: { label: 'Invoices YTD (pre-tax)', format: 'currency', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  invoicesMtd: { label: 'Invoice count MTD', format: 'number', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  invoicesYtd: { label: 'Invoice count YTD', format: 'number', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  gstMtd: { label: 'GST amount MTD', format: 'currency', direction: 0, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  gstYtd: { label: 'GST amount YTD', format: 'currency', direction: 0, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  revenueGrowthPercent: { label: 'Revenue growth', format: 'signedPercent', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  salesTarget: { label: 'Sales target', format: 'currency', direction: 1, group: 'Revenue & pipeline' },
  weightedPipeline: { label: 'Weighted pipeline', format: 'currency', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  openPipelineValue: { label: 'Open pipeline', format: 'currency', direction: 1, group: 'Revenue & pipeline', to: `${R}/sales-pipeline` },
  receivablesOutstanding: { label: 'Receivables outstanding', format: 'currency', direction: -1, group: 'Receivables', to: `${R}/customer-collection` },
  receivablesOverdue: { label: 'Receivables overdue', format: 'currency', direction: -1, group: 'Receivables', to: `${R}/customer-collection` },
  receivablesOver90: { label: 'Overdue 90+ days', format: 'currency', direction: -1, group: 'Receivables', to: `${R}/customer-collection` },
  dsoDays: { label: 'DSO', format: 'days', direction: -1, group: 'Receivables', to: `${R}/customer-collection` },
  collectionsPeriod: { label: 'Collections (period)', format: 'currency', direction: 1, group: 'Receivables', to: `${R}/customer-collection` },
  activeProjects: { label: 'Active projects', format: 'number', direction: 0, group: 'Delivery & people', to: `${R}/project-portfolio` },
  overdueProjects: { label: 'Overdue projects', format: 'number', direction: -1, group: 'Delivery & people', to: `${R}/project-portfolio` },
  headcount: { label: 'Headcount', format: 'number', direction: 0, group: 'Delivery & people', to: `${R}/resource-capacity` },
  utilizationPercent: { label: 'Utilisation', format: 'percent', direction: 1, group: 'Delivery & people', to: `${R}/resource-capacity` },
  benchEmployees: { label: 'Bench', format: 'number', direction: -1, group: 'Delivery & people', to: `${R}/resource-capacity` },
  renewalsDue30: { label: 'Renewals due (30d)', format: 'number', direction: -1, group: 'Commitments & governance', to: `${R}/renewals` },
  monthlyCommitment: { label: 'Monthly commitments', format: 'currency', direction: -1, group: 'Commitments & governance', to: `${R}/renewals` },
  openRisks: { label: 'Open risks', format: 'number', direction: -1, group: 'Commitments & governance', to: `${R}/risk-actions` },
  redRisks: { label: 'Red risks', format: 'number', direction: -1, group: 'Commitments & governance', to: `${R}/risk-actions` },
  overdueActions: { label: 'Overdue actions', format: 'number', direction: -1, group: 'Commitments & governance', to: `${R}/risk-actions` },
  complianceGaps: { label: 'Compliance gaps', format: 'number', direction: -1, group: 'Commitments & governance', to: `${R}/risk-actions` }
}

export const kpiMeta = (key) => KPI_META[key] || { label: humanize(key), format: 'number', direction: 0, group: 'Other' }

/** Group a kpis object into [{ group, items: [{ key, metric, meta }] }] preserving catalogue order. */
export const groupKpis = (kpis = {}) => {
  const groups = new Map()
  const keys = [...Object.keys(KPI_META).filter((k) => k in kpis), ...Object.keys(kpis).filter((k) => !(k in KPI_META))]
  keys.forEach((key) => {
    const meta = kpiMeta(key)
    if (!groups.has(meta.group)) groups.set(meta.group, [])
    groups.get(meta.group).push({ key, metric: kpis[key], meta })
  })
  return [...groups.entries()].map(([group, items]) => ({ group, items }))
}

export const SIGNAL_ROUTES = {
  runway: `${R}/financial-health`,
  receivables: `${R}/customer-collection`,
  budget: `${R}/expense-budget`,
  projects: `${R}/project-portfolio`,
  utilization: `${R}/resource-capacity`,
  renewals: `${R}/renewals`,
  risks: `${R}/risk-actions`,
  actions: `${R}/risk-actions`
}

/** improved | deteriorated | unchanged | changed | unavailable for a compare-API KPI diff row. */
export const kpiTrend = (key, diff) => {
  if (!diff || diff.status !== 'actual' || diff.delta === null || diff.delta === undefined) return 'unavailable'
  if (diff.delta === 0) return 'unchanged'
  const dir = kpiMeta(key).direction
  if (!dir) return 'changed'
  return Math.sign(diff.delta) === dir ? 'improved' : 'deteriorated'
}

// ---------- health ----------

const HEALTH_CLASSES = {
  Green: 'mis-badge mis-badge-green',
  Amber: 'mis-badge mis-badge-amber',
  Red: 'mis-badge mis-badge-red',
  Grey: 'mis-badge mis-badge-grey'
}

const HEALTH_ALIASES = {
  green: 'Green', good: 'Green', actual: 'Green', low: 'Green', under: 'Green', completed: 'Green', closed: 'Green',
  amber: 'Amber', yellow: 'Amber', partial: 'Amber', medium: 'Amber', mitigating: 'Amber', 'in progress': 'Amber',
  red: 'Red', high: 'Red', critical: 'Red', over: 'Red', overdue: 'Red',
  grey: 'Grey', gray: 'Grey', unavailable: 'Grey', missing: 'Grey', masked: 'Grey', no_budget: 'Grey', open: 'Amber', cancelled: 'Grey'
}

export const normalizeHealth = (status) => {
  if (!status) return 'Grey'
  if (HEALTH_CLASSES[status]) return status
  return HEALTH_ALIASES[String(status).toLowerCase()] || 'Grey'
}

/** { className, label } for a Green/Amber/Red/Grey (or alias) status. */
export const healthBadge = (status, label) => {
  const tone = normalizeHealth(status)
  return { className: HEALTH_CLASSES[tone], label: label || (tone === 'Grey' && !status ? 'No data' : String(status || tone)), tone }
}

// ---------- period helpers ----------

const pad = (n) => String(n).padStart(2, '0')
export const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const todayISO = () => toISODate(new Date())

const fyStartYearOf = (d) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1)
export const fyLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(-2)}`

export const getCurrentFY = (date = new Date()) => fyLabel(fyStartYearOf(date))

export const getFYOptions = (count = 5, date = new Date()) => {
  const start = fyStartYearOf(date)
  return Array.from({ length: count }, (_, i) => fyLabel(start - i))
}

const parseISO = (v) => {
  if (!v) return new Date()
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? new Date() : d
}

export const getMonthRange = (asOf) => {
  const d = parseISO(asOf)
  return { from: new Date(d.getFullYear(), d.getMonth(), 1), to: d }
}

export const getQuarterRange = (asOf) => {
  const d = parseISO(asOf)
  const offset = (d.getMonth() - 3 + 12) % 3
  return { from: new Date(d.getFullYear(), d.getMonth() - offset, 1), to: d }
}

export const getYtdRange = (asOf) => {
  const d = parseISO(asOf)
  return { from: new Date(fyStartYearOf(d), 3, 1), to: d }
}

export const getPeriodRange = (viewType, asOf) => {
  if (viewType === 'quarterly') return getQuarterRange(asOf)
  if (viewType === 'ytd') return getYtdRange(asOf)
  return getMonthRange(asOf)
}

export const describePeriod = (viewType, asOf) => {
  const { from, to } = getPeriodRange(viewType, asOf)
  return `${formatDate(from)} – ${formatDate(to)}`
}

// ---------- filters ----------

export const DEFAULT_FILTERS = Object.freeze({
  viewType: 'monthly',
  asOf: '',
  financialYear: '',
  customer: '',
  project: ''
})

export const loadStoredFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY)
    if (!raw) return { ...DEFAULT_FILTERS }
    return { ...DEFAULT_FILTERS, ...JSON.parse(raw) }
  } catch {
    return { ...DEFAULT_FILTERS }
  }
}

export const saveStoredFilters = (filters) => {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // sessionStorage may be unavailable (private mode); filters simply won't persist
  }
}

/** Backend only accepts customer/project ObjectIds; free text is applied client-side. */
export const toApiParams = (filters = {}) => {
  const params = { viewType: filters.viewType || 'monthly' }
  if (filters.asOf) params.asOf = filters.asOf
  if (filters.financialYear) params.financialYear = filters.financialYear
  if (OBJECT_ID_RE.test(String(filters.customer || '').trim())) params.customerId = filters.customer.trim()
  if (OBJECT_ID_RE.test(String(filters.project || '').trim())) params.projectId = filters.project.trim()
  return params
}

const textTerm = (v) => {
  const s = String(v || '').trim().toLowerCase()
  return s && !OBJECT_ID_RE.test(s) ? s : ''
}

/** Filter rows by the free-text customer/project filter (matches any of the given fields). */
export const applyTextFilters = (rows, filters, { customerFields = ['customerName', 'customer'], projectFields = ['projectName'] } = {}) => {
  if (!Array.isArray(rows)) return []
  const c = textTerm(filters?.customer)
  const p = textTerm(filters?.project)
  if (!c && !p) return rows
  const matches = (row, fields, term) => fields.some((f) => String(row?.[f] ?? '').toLowerCase().includes(term))
  return rows.filter((row) => {
    const hasC = customerFields.some((f) => row && f in row)
    const hasP = projectFields.some((f) => row && f in row)
    if (c && hasC && !matches(row, customerFields, c)) return false
    if (p && hasP && !matches(row, projectFields, p)) return false
    return true
  })
}

// ---------- completeness ----------

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)

/** Share of metrics in a payload that have live values (actual or zero). */
export const computeCompleteness = (data) => {
  let total = 0
  let available = 0
  const walk = (node, depth) => {
    if (depth > 8 || !node) return
    if (Array.isArray(node)) return
    if (isMetric(node)) {
      total += 1
      if (node.status === 'actual' || node.status === 'zero') available += 1
      return
    }
    if (isPlainObject(node)) Object.values(node).forEach((v) => walk(v, depth + 1))
  }
  walk(data, 0)
  return { total, available, percent: total ? Math.round((available / total) * 100) : null }
}

// ---------- export / print ----------

export const humanize = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b(\d+)to(\d+)\b/gi, '$1–$2')
    .replace(/^./, (c) => c.toUpperCase())

const exportCell = (v) => {
  if (v === null || v === undefined) return ''
  if (isMetric(v)) {
    if (v.status === 'actual' || v.status === 'zero') return v.value
    return UNAVAILABLE_TEXT
  }
  if (v instanceof Date) return formatDate(v)
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : x)).join(', ')
  if (typeof v === 'object') {
    if ('name' in v) return v.name
    if ('label' in v && 'amount' in v) return exportCell(v.amount)
    return JSON.stringify(v)
  }
  return v
}

const normalizeRows = (rows) =>
  (rows || []).map((row) => {
    const out = {}
    Object.entries(row || {}).forEach(([k, v]) => {
      if (k === '__v' || k === 'company') return
      out[humanize(k)] = exportCell(v)
    })
    return out
  })

/** Flatten a report payload into { Section, Metric, Value, Status, Note } rows. */
export const flattenMetrics = (data, prefix = '') => {
  const rows = []
  const walk = (node, path, depth) => {
    if (depth > 6 || node === null || node === undefined) return
    if (Array.isArray(node)) return
    if (isMetric(node)) {
      const parts = path.split('.')
      rows.push({
        Section: parts.slice(0, -1).map(humanize).join(' › ') || '—',
        Metric: humanize(parts[parts.length - 1] || ''),
        Value: node.status === 'actual' || node.status === 'zero' ? node.value : UNAVAILABLE_TEXT,
        Status: node.status,
        Note: node.status === 'masked' ? 'Restricted' : node.note || ''
      })
      return
    }
    if (isPlainObject(node)) {
      Object.entries(node).forEach(([k, v]) => {
        if (k === 'period') return
        walk(v, path ? `${path}.${k}` : k, depth + 1)
      })
    }
  }
  walk(data, prefix, 0)
  return rows
}

const safeSheetName = (name, used) => {
  let base = String(name || 'Sheet').replace(/[\\/?*[\]:]/g, ' ').slice(0, 28) || 'Sheet'
  let candidate = base
  let i = 2
  while (used.has(candidate)) candidate = `${base.slice(0, 25)} ${i++}`
  used.add(candidate)
  return candidate
}

const downloadCsv = (filename, rows) => {
  const data = normalizeRows(rows)
  const headers = [...new Set(data.flatMap((r) => Object.keys(r)))]
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = [headers.map(esc).join(','), ...data.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Export to .xlsx. `rows` may be an array of row objects (single sheet) or an
 * object of { sheetName: rows[] } for a multi-sheet workbook.
 * Falls back to CSV (first sheet) if the XLSX writer fails.
 */
export const exportToSpreadsheet = (filename, rows) => {
  const sheets = Array.isArray(rows) ? { Report: rows } : rows || {}
  const stamp = todayISO()
  const base = `${filename}-${stamp}`
  try {
    const wb = XLSX.utils.book_new()
    const used = new Set()
    Object.entries(sheets).forEach(([name, list]) => {
      const data = normalizeRows(list)
      const ws = XLSX.utils.json_to_sheet(data.length ? data : [{ Info: 'No records' }])
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName(name, used))
    })
    XLSX.writeFile(wb, `${base}.xlsx`)
  } catch (err) {
    console.error('XLSX export failed, falling back to CSV', err)
    const first = Object.values(sheets)[0] || []
    downloadCsv(base, first)
  }
}

export const printReport = () => {
  window.print()
}

export const getErrorMessage = (err, fallback = 'Failed to load report') => {
  const status = err?.response?.status
  if (status === 403) return 'You do not have Management MIS access for this report. Contact your administrator.'
  return err?.response?.data?.message || err?.message || fallback
}
